import { useEffect, useState } from "react";
import { Banknote, Snowflake, Sun, History } from "lucide-react";
import { api, formatTL, formatDate, formatApiError, SUB_STATUS, PAYMENT_STATUS, PACKAGE_STYLE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PackageRequestsPanel } from "@/components/PackageRequestsPanel";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const FILTERS = [["ALL", "Tümü"], ["ACTIVE", "Aktif"], ["WARNING", "Süre Doluyor"], ["FROZEN", "Donduruldu"]];

export default function AdminSubscriptionsPage() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("SUBSCRIPTION_MANAGE");
  const [rows, setRows] = useState([]);
  const [packages, setPackages] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [detail, setDetail] = useState(null);
  const [days, setDays] = useState("");

  const load = async () => {
    const [{ data }, { data: p }] = await Promise.all([api.get("/admin/subscriptions"), api.get("/packages")]);
    setRows(data);
    setPackages(p.packages);
  };
  useEffect(() => { load(); }, []);

  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openDetail = async (r) => {
    const { data } = await api.get(`/admin/offices/${r.office_id}/payments`);
    setDetail({ ...r, ...data });
  };

  const counts = Object.fromEntries(FILTERS.map(([k]) => [k, k === "ALL" ? rows.length : rows.filter((r) => r.status === k).length]));
  const shown = filter === "ALL" ? rows : rows.filter((r) => r.status === filter);

  return (
    <div className="space-y-6" data-testid="admin-subscriptions-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Süper Admin</div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Abonelik Takibi</h1>
        <p className="text-slate-500 mt-1">Ofislerin abonelik süresi, ödeme durumu ve paketleri.</p>
      </div>

      <PackageRequestsPanel canManage={canManage} packages={packages} onDecided={load} />

      <div className="flex gap-2 flex-wrap">
        {FILTERS.map(([k, l]) => (
          <Button key={k} data-testid={`filter-${k}`} size="sm" variant={filter === k ? "default" : "outline"} onClick={() => setFilter(k)}>
            {l} ({counts[k]})
          </Button>
        ))}
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              {["Ofis", "Paket", "Dönem", "Kalan", "Durum", "Ödeme", "Son Ödeme", "İşlemler"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.office_id} data-testid={`sub-row-${r.office_code}`} className="border-t hover:bg-slate-50">
                <td className="px-4 py-3"><div className="font-semibold">{r.office_name}</div><div className="font-mono text-xs text-slate-500">{r.office_code}</div></td>
                <td className="px-4 py-3">
                  <Select disabled={!canManage} value={r.package_code} onValueChange={(v) => run(() => api.post(`/admin/offices/${r.office_id}/package`, { package_code: v }), "Paket değiştirildi")}>
                    <SelectTrigger data-testid={`sub-package-${r.office_code}`} className={`h-8 w-28 ${PACKAGE_STYLE[r.package_code]}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{packages.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">{formatDate(r.period_start)} – {formatDate(r.period_end)}<div>{r.day_of_period}. gün</div></td>
                <td className="px-4 py-3 font-semibold">{r.days_left} gün</td>
                <td className="px-4 py-3"><Badge variant="outline" className={SUB_STATUS[r.status].cls}>{SUB_STATUS[r.status].label}</Badge></td>
                <td className="px-4 py-3">{PAYMENT_STATUS[r.payment_status]}{r.discount && <div className="text-xs text-emerald-700">%{r.discount.percent} kampanya</div>}</td>
                <td className="px-4 py-3 text-xs">{r.last_payment ? <>{formatDate(r.last_payment.paid_at)}<div className="font-semibold">{formatTL(r.last_payment.amount)}</div></> : "-"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {canManage && <>
                    <Button size="sm" data-testid={`record-payment-${r.office_code}`} className="bg-emerald-600 hover:bg-emerald-700 h-8"
                      onClick={() => run(() => api.post(`/admin/offices/${r.office_id}/payments`, {}), "Ödeme alındı, süre 30 gün uzatıldı")}>
                      <Banknote className="w-3.5 h-3.5 mr-1" />Ödeme Al
                    </Button>
                    <Button size="sm" variant="outline" className="h-8" data-testid={`freeze-${r.office_code}`} title={r.manual_frozen ? "Çöz" : "Dondur"}
                      onClick={() => run(() => api.post(`/admin/offices/${r.office_id}/freeze`, { frozen: !r.manual_frozen }), r.manual_frozen ? "Hesap açıldı" : "Hesap donduruldu")}>
                      {r.manual_frozen ? <Sun className="w-3.5 h-3.5" /> : <Snowflake className="w-3.5 h-3.5" />}
                    </Button>
                    </>}
                    <Button size="sm" variant="outline" className="h-8" data-testid={`history-${r.office_code}`} onClick={() => openDetail(r)}><History className="w-3.5 h-3.5" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {shown.length === 0 && <div className="p-10 text-center text-slate-400">Kayıt yok.</div>}
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{detail?.office_name} — Abonelik Geçmişi</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-5 text-sm">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <div className="text-xs text-slate-500 mb-1">Süreyi gün olarak ayarla (eksi değer kısaltır)</div>
                  <Input data-testid="adjust-days-input" type="number" value={days} onChange={(e) => setDays(e.target.value)} placeholder="Örn: 7 veya -5" />
                </div>
                <Button data-testid="adjust-days-btn" onClick={() => run(async () => { await api.post(`/admin/offices/${detail.office_id}/adjust-period`, { days: parseInt(days, 10) }); setDetail(null); setDays(""); }, "Süre güncellendi")} disabled={!days}>Uygula</Button>
              </div>
              <div>
                <div className="font-semibold mb-2">Ödemeler</div>
                {detail.payments.length === 0 ? <div className="text-slate-400">Yok</div> : detail.payments.map((p) => (
                  <div key={p.id} className="flex justify-between border-b py-2"><span>{formatDate(p.paid_at)} · {p.package_code}{p.campaign && ` · %${p.campaign.percent} ${p.campaign.name}`}</span><b>{formatTL(p.amount)}</b></div>
                ))}
              </div>
              <div>
                <div className="font-semibold mb-2">Paket Geçişleri</div>
                {detail.changes.length === 0 ? <div className="text-slate-400">Yok</div> : detail.changes.map((c) => (
                  <div key={c.id} className="flex justify-between border-b py-2">
                    <span>{formatDate(c.created_at)} · {c.from_package} → {c.to_package}</span>
                    <Badge variant="outline">{c.direction === "UPGRADE" ? "Yükseltme" : "Düşürme"}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
